// Quick-note composer (mock s4: "הערה מהירה… Enter לשמירה") — Enter saves,
// Shift+Enter keeps typing, optional 📌 toggle writes the new `pinned` flag.
import { useCallback, useRef, useState } from "react";
import { IconButton, InputAdornment, TextField, Tooltip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { PushPin, PushPinOutlined } from "@material-ui/icons";
import { useSnackbar } from "notistack5";

type QuickNoteComposerProps = {
  placeholder: string;
  addNote: (text: string, options?: { pinned?: boolean }) => Promise<void>;
  /** Hide the pin toggle (the activity-panel composer keeps it minimal). */
  withPinToggle?: boolean;
};

export function QuickNoteComposer({
  placeholder,
  addNote,
  withPinToggle = true
}: QuickNoteComposerProps): JSX.Element {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [text, setText] = useState("");
  const [pinned, setPinned] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSave = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Optimistic: clear immediately, restore on failure.
    setText("");
    setPinned(false);
    addNote(trimmed, { pinned }).catch(() => {
      setText(trimmed);
      enqueueSnackbar("שמירת ההערה נכשלה", { variant: "error", autoHideDuration: 4000 });
    });
    inputRef.current?.focus();
  }, [text, pinned, addNote, enqueueSnackbar]);

  // `dir="auto"` resolves direction from the VALUE — while the field is empty
  // there is nothing to resolve, so it falls back to LTR and the Hebrew
  // placeholder ("הוסף הערה או תיעוד שיחה…") hugs the LEFT edge. Anchor the
  // empty field to the UI direction, and hand control back to `auto` the moment
  // there is text, so a pasted LTR value (phone number, URL, English) still
  // renders LTR. Same rule as notes/NoteComposer — `theme.direction`, never a
  // hardcoded "rtl", so the LTR build stays correct.
  const composerDir = text.trim() ? "auto" : theme.direction;

  return (
    <TextField
      fullWidth
      size="small"
      value={text}
      inputRef={inputRef}
      placeholder={placeholder}
      onChange={(event) => setText(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          handleSave();
        }
      }}
      inputProps={{ dir: composerDir, "aria-label": placeholder }}
      InputProps={{
        endAdornment: withPinToggle ? (
          <InputAdornment position="end">
            <Tooltip title={pinned ? "תישמר כהערה נעוצה" : "נעיצת ההערה"}>
              <IconButton
                size="small"
                edge="end"
                onClick={() => setPinned((prev) => !prev)}
                color={pinned ? "primary" : "default"}
                aria-label={pinned ? "ביטול נעיצה" : "נעיצת ההערה"}
              >
                {pinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ) : undefined
      }}
    />
  );
}
